import React, { useState, useMemo } from "react";
import { Table, DatePicker } from "antd";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import dayjs from "dayjs";
import { ReportPage } from "../../../components";
import { GetSalesReportEmployeePerformance } from "../../../Api";
import { exportToCSV, formatLKR } from "../../../lib";
import { useSectionAccess } from "../../../Services";
import { ReportAction } from "../../../Types";

const { RangePicker } = DatePicker;

export const EmployeePerformanceReport: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "day"),
    dayjs(),
  ]);

  const startDateStr = dateRange[0].format("YYYY-MM-DD");
  const endDateStr = dateRange[1].format("YYYY-MM-DD");

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["employee-performance", startDateStr, endDateStr],
    queryFn: () =>
      GetSalesReportEmployeePerformance({
        start_date: startDateStr,
        end_date: endDateStr,
      }),
  });

  const reportData = useMemo(() => responseData?.data || [], [responseData]);
  const { isAllowed } = useSectionAccess("reports");

  const stats = useMemo(() => {
    const topUser = reportData.length > 0 ? reportData[0].employee_name : "N/A";
    const salesSum = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_sales_amount || 0),
      0,
    );
    const profitSum = reportData.reduce(
      (sum: number, item: any) => sum + (item.total_profit_amount || 0),
      0,
    );
    return [
      {
        title: "Top Sales Executive",
        value: topUser,
        colorClass: "text-indigo-600 font-extrabold",
      },
      {
        title: "Aggregate Team Revenue",
        value: formatLKR(salesSum),
        colorClass: "text-gray-600",
      },
      {
        title: "Aggregate Gross Profit Generated",
        value: formatLKR(profitSum),
        colorClass: "text-emerald-600",
      },
    ];
  }, [reportData]);

  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (v: string, record: any) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-800">{v}</span>
          <span className="text-[10px] text-gray-400 font-medium">
            User: @{record.username}
          </span>
        </div>
      ),
    },
    {
      title: "Total Bills Logged",
      dataIndex: "total_sales_count",
      key: "total_sales_count",
      align: "center" as const,
      render: (v: number) => (
        <span className="text-xs font-bold text-gray-700">{v} Sales</span>
      ),
    },
    {
      title: "Revenue Logged",
      dataIndex: "total_sales_amount",
      key: "total_sales_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-indigo-700">
          LKR {v.toLocaleString()}
        </span>
      ),
    },
    {
      title: "Gross Profit Generated",
      dataIndex: "total_profit_amount",
      key: "total_profit_amount",
      align: "right" as const,
      render: (v: number) => (
        <span className="text-xs font-extrabold text-emerald-600">
          LKR {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Expenses logged",
      dataIndex: "total_expenses_amount",
      key: "total_expenses_amount",
      align: "right" as const,
      render: (v: number) => (
        <span
          className={`text-xs font-medium ${v > 0 ? "text-rose-500 font-semibold" : "text-gray-400"}`}
        >
          {v > 0 ? `LKR ${v.toLocaleString()}` : "—"}
        </span>
      ),
    },
  ];

  const filterControls = (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-col gap-1.5 font-sans">
        <span className="text-[10px] font-bold text-gray-400 uppercase">
          Reporting Date Period
        </span>
        <RangePicker
          value={dateRange}
          onChange={(dates) =>
            dates && dates[0] && dates[1] && setDateRange([dates[0], dates[1]])
          }
          allowClear={false}
          className="h-9 rounded-lg border border-gray-200 shadow-sm text-xs font-semibold"
        />
      </div>
    </div>
  );

  return (
    <ReportPage
      title="Employee Performance"
      description="Track total sales count, volumes, margins, and expenses logged per staff member."
      icon={<Users className="w-6 h-6 text-indigo-600" />}
      backPath="/system/sales-reports"
      stats={stats}
      onExportCSV={
        isAllowed(ReportAction.Export)
          ? () => {
              exportToCSV({
                data: reportData,
                fileName: "cash_flow_export",
                headers: ["date", "description", "income", "expense", "balance"],
              });
            }
          : undefined
      }
      isExportDisabled={reportData.length === 0 || !isAllowed(ReportAction.Export)}
      onRefresh={refetch}
      filterControls={filterControls}
    >
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1">
        <Table
          dataSource={reportData}
          columns={columns}
          loading={isLoading}
          rowKey={(record: any) => record.username || Math.random().toString()}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            className: "px-6 pb-2",
          }}
          scroll={{ x: 1000 }}
          className="custom-report-table"
        />
      </div>
    </ReportPage>
  );
};
